import { action, computed, observable } from 'mobx';
import math from 'exact-math';
import FormatService from 'modules/Format';
import {
  PositionBase,
  SurveillanceAlertAcceptedObject,
} from 'modules/TradesPW3/Positions/typings';
import Calculation from 'modules/TradesPW3/Positions/helpers/positionsCalculation';
import { POSITION_TYPE } from 'modules/TradesPW3/Positions/constants';
import { OrderSide } from 'modules/Ordering';

export default class PositionsPW3 {
  @observable positions: PositionBase[] = [];
  @observable closingPositionId = '';
  @observable convertingPositionId = '';
  @observable surveillanceAlertAccepted: SurveillanceAlertAcceptedObject = {};
  @observable dailyPnL = '';
  @observable totalPnL = '';

  @action setClosingPositionId = (positionId: string) => {
    this.closingPositionId = positionId;
  };

  @action setConvertingPositionId = (positionId: string) => {
    this.convertingPositionId = positionId;
  };

  @action setPositions = (positions: PositionBase[]) => {
    this.positions = positions;
  };

  @action setSurveillanceAlertAccepted = (
    positionId: string,
    status: boolean,
  ) => {
    this.surveillanceAlertAccepted[positionId] = status;
  };

  @action clearSurveillanceAlertAccepted = () => {
    this.surveillanceAlertAccepted = {};
  };

  @computed get groupedPositionsByType() {
    const openPositions: PositionBase[] = [];
    const closedPositions: PositionBase[] = [];

    this.positions.forEach(position => {
      const positionType = this.getPositionType(position);

      if (positionType === POSITION_TYPE.Opened) {
        // if position qty equal zero,
        // accept this postions as closed otherwise - as open
        openPositions.push(position);
      } else {
        closedPositions.push(position);
      }
    });

    return {
      closedPositions,
      openPositions,
    };
  }

  @computed get exchangeTokens() {
    return this.positions.map(position =>
      FormatService.generateExchangeToken(
        position.instrument.e,
        position.instrument.t,
      ),
    );
  }

  getPositionByUniqueId = (positions: PositionBase[], uniqueId: string) =>
    positions.find(
      position => uniqueId === this.generatePositionUniqueId(position),
    );

  generatePositionUniqueId = (position: PositionBase) =>
    `${position.instrument.e}|${position.instrument.t}|${position.product}`;

  getPositionType = (position: PositionBase) =>
  (this.getNetQty(position) === 0
      ? POSITION_TYPE.Closed
      : POSITION_TYPE.Opened);

  getNetQty = (position: PositionBase) =>
    math.sub(position.netInfo.buyQty, position.netInfo.sellQty);

  getPositionSide = (singlePosition: PositionBase): OrderSide | null => {
    const calc = new Calculation();

    const { netQty } = calc.getNetQuantity(singlePosition);

    let netSide: OrderSide | null = netQty > 0 ? OrderSide.Buy : OrderSide.Sell;

    if (netQty === 0) {
      netSide = null;
    }

    return netSide;
  };

  @action setPositionsPnL = (dailyPnL: string, totalPnL: string) => {
    this.dailyPnL = dailyPnL;
    this.totalPnL = totalPnL;
  }
}
